import { useCallback, useEffect, useState } from "react";
import { Loader2, RotateCcw, Save, Fuel, Zap, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CitySelector from "@/components/CitySelector";

const FUEL_KEYS = ["Petrol", "Diesel", "CNG"];

/**
 * FuelEnergyRatesPanel — v3 Item 2.
 * Auto-fetches suggested rates when the user's default city changes.
 * Allows editing + Reset to City Rate per fuel.
 */
export default function FuelEnergyRatesPanel() {
  const { user, refreshMe } = useAuth();
  const city = user?.preferred_city;
  const [fuel, setFuel] = useState(null);
  const [evHome, setEvHome] = useState(null);
  const [evPublic, setEvPublic] = useState([]);
  const [drafts, setDrafts] = useState({}); // {Petrol: '112.5', ...}
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!city) return;
    setLoading(true);
    try {
      const [f, e, p] = await Promise.all([
        api.get(`/rates/fuel?city=${encodeURIComponent(city)}`),
        api.get(`/rates/ev-home?city=${encodeURIComponent(city)}`),
        api.get(`/rates/ev-public`),
      ]);
      setFuel(f.data);
      setEvHome(e.data);
      setEvPublic(p.data.operators || []);
      setDrafts({}); // reset unsaved edits when city changes
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [city]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!city) return;
    if (Object.keys(drafts).length === 0) return;
    setSaving(true);
    try {
      const body = { city };
      for (const [k, v] of Object.entries(drafts)) {
        if (v === "" || v == null) continue;
        body[k.toLowerCase()] = parseFloat(v);
      }
      await api.put("/rates/fuel/override", body);
      toast.success("Custom rates saved");
      setDrafts({});
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!city) return;
    setSaving(true);
    try {
      await api.delete(`/rates/fuel/override?city=${encodeURIComponent(city)}`);
      toast.success("Reset to city rates");
      setDrafts({});
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!city) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--border-line)] p-6 text-sm text-[var(--text-muted)]">
        Set a default city first (Profile → Default city) to load suggested rates.
      </div>
    );
  }

  const isCustom = !!fuel?.custom;

  return (
    <div className="rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-6 space-y-5" data-testid="fuel-energy-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="ft-overline">FUEL &amp; ENERGY RATES</p>
          <h3 className="font-display text-2xl font-bold tracking-tight">Suggested prices for {city}</h3>
          <p className="mt-1 text-sm text-[var(--text-muted)]">
            Auto-refreshes when you change your default city. Edit any rate to save your own — Reset to fall back to the city suggestion.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CitySelector value={city} onChange={() => {}} compact testid="rates-city-picker" />
          {loading && <Loader2 size={14} className="animate-spin text-[var(--text-muted)]"/>}
        </div>
      </div>

      {/* Fuel prices */}
      <div className="rounded-lg border border-[var(--border-line)] bg-[var(--bg-elevated)] p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Fuel size={14} className="text-[#FF5A1F]" /> Fuel
          </div>
          <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">
            {fuel?.estimated ? "Estimate" : "Source"}: {fuel?.source || "—"} · Updated {fuel?.effective_date ? new Date(fuel.effective_date).toLocaleDateString("en-IN") : "—"}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {FUEL_KEYS.map((k) => {
            const suggested = (fuel?.rates || {})[k];
            const value = drafts[k] !== undefined ? drafts[k] : (suggested != null ? String(suggested) : "");
            return (
              <label key={k} className="block">
                <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{k} (₹/L{k === "CNG" ? " or /kg" : ""})</span>
                <input
                  type="number" step="0.01"
                  className="input mt-1"
                  data-testid={`rate-${k.toLowerCase()}`}
                  value={value}
                  onChange={(e) => setDrafts((d) => ({ ...d, [k]: e.target.value }))}
                  placeholder={suggested != null ? String(suggested) : "—"}
                />
              </label>
            );
          })}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={save}
            disabled={saving || Object.keys(drafts).length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#FF5A1F] hover:bg-[#E64A0F] text-white text-xs font-semibold disabled:opacity-60"
            data-testid="save-rates-btn"
          >
            {saving ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Save custom rates
          </button>
          {isCustom && (
            <button
              onClick={reset}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[var(--border-line)] text-xs hover:bg-[var(--hover)]"
              data-testid="reset-rates-btn"
              title="Discard the custom rate and use the current suggested rate for this city"
            >
              <RotateCcw size={12}/> Reset to City Rate
            </button>
          )}
          {isCustom && (
            <span className="ml-auto text-[10px] uppercase tracking-wider text-[#FF5A1F]">Using your custom rate</span>
          )}
        </div>
        {fuel?.cng_operator && (
          <p className="mt-2 text-xs text-[var(--text-muted)]">CNG operator for this city: <span className="text-white">{fuel.cng_operator}</span></p>
        )}
      </div>

      {/* EV home */}
      <div className="rounded-lg border border-[var(--border-line)] bg-[var(--bg-elevated)] p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Zap size={14} className="text-[#22D3EE]" /> Home electricity
          </div>
          <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">
            {evHome?.estimated ? "Estimate" : "Source"}: {evHome?.source || "—"} · Updated {evHome?.effective_date || "—"}
          </div>
        </div>
        {evHome ? (
          <div className="flex items-center gap-4 text-sm">
            <span className="inline-flex items-center gap-1 text-[var(--text-muted)]"><MapPin size={12}/> {evHome.city}</span>
            <span>DISCOM: <span className="text-white">{evHome.discom || "—"}</span></span>
            <span className="font-mono text-lg text-[#22D3EE]">
              {evHome.kwh_rate != null ? `₹${evHome.kwh_rate}/kWh` : "—"}
            </span>
          </div>
        ) : (
          <p className="text-sm text-[var(--text-muted)]">Loading…</p>
        )}
        <p className="mt-2 text-xs text-[var(--text-muted)]">
          You can override the rate on each individual charging session (Add Charging → ₹/kWh).
        </p>
      </div>

      {/* EV public */}
      <div className="rounded-lg border border-[var(--border-line)] bg-[var(--bg-elevated)] p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Zap size={14} className="text-[#F472B6]" /> Public charging
          </div>
          <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Sample tariffs</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
          {evPublic.slice(0, 9).map((o) => (
            <div key={o.key} className="rounded-md border border-[var(--border-line)] p-2 flex justify-between">
              <span>{o.name}</span>
              <span className="text-[#F472B6] font-mono">₹{o.dc_kwh}/kWh</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
